import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'react-hot-toast';
import { Save, Plus, Trash2, Loader2, ArrowLeft, FileText, Package, Calculator, Truck, Info } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuotationCreate = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('header');
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  
  // Master Data
  const [customers, setCustomers] = useState([]);
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [distChannels, setDistChannels] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [plants, setPlants] = useState([]);
  const [pricingProcedures, setPricingProcedures] = useState([]);
  const [incoterms, setIncoterms] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  
  // Customer-specific data
  const [customerSalesAreas, setCustomerSalesAreas] = useState([]);
  const [salesAreaError, setSalesAreaError] = useState('');
  const [shippingPointsForPlant, setShippingPointsForPlant] = useState({});

  const [header, setHeader] = useState({
    document_type: 'QT',
    customer_id: '',
    sales_organization: '',
    distribution_channel: '',
    division: '',
    quotation_date: new Date().toISOString().split('T')[0],
    pricing_date: new Date().toISOString().split('T')[0],
    requested_delivery_date: '',
    valid_from: new Date().toISOString().split('T')[0],
    valid_to: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    pricing_procedure: 'ZSTD01',
    currency: 'USD',
    sold_to_party: '',
    ship_to_party: '',
    payment_terms: '',
    incoterms: '',
    incoterms_location: '',
    customer_reference: '',
    header_note: ''
  });

  const [items, setItems] = useState([{
    item_number: 10,
    material_id: '',
    item_description: '',
    item_category: 'TAN',
    quoted_quantity: 1,
    unit_of_measure: 'EA',
    net_price: 0,
    plant: '',
    plant_determined_from: '',
    shipping_point: '',
    pricing_info: '',
    availability_check: false,
    item_note: ''
  }]);

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    setIsLoading(true);
    try {
      const [custRes, orgRes, dcRes, divRes, matRes, plantRes, procRes, incoRes, payRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/sales-organizations`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/distribution-channels`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/divisions`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/materials`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/material-management/plants`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/pricing/pricing-procedures`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/customer-incoterms`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/customer-terms-of-payments`).catch(() => ({ data: [] }))
      ]);
      setCustomers(custRes.data || []);
      setSalesOrgs(orgRes.data || []);
      setDistChannels(dcRes.data || []);
      setDivisions(divRes.data || []);
      setMaterials(matRes.data || []);
      setPlants(plantRes.data || []);
      setPricingProcedures(procRes.data || [{ pricing_procedure_id: 'ZSTD01', description: 'Standard Pricing' }]);
      setIncoterms(incoRes.data || []);
      setPaymentTerms(payRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch commercial terms from Ship-to Party's customer master
  const fetchCommercialTerms = async (shipToPartyId, salesOrg, distChannel) => {
    if (!shipToPartyId || !salesOrg || !distChannel) return;
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/presales/customer-commercial-terms/${shipToPartyId}`,
        { params: { sales_org: salesOrg, dist_channel: distChannel } }
      );
      
      if (response.data) {
        setHeader(prev => ({
          ...prev,
          incoterms: response.data.incoterms || '',
          incoterms_location: response.data.incoterms_location || '',
          payment_terms: response.data.payment_terms || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching commercial terms:', error);
    }
  };

  // Fetch shipping points for a plant
  const fetchShippingPointsForPlant = async (plantId) => {
    if (!plantId || shippingPointsForPlant[plantId]) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/presales/shipping-points-for-plant/${plantId}`);
      setShippingPointsForPlant(prev => ({
        ...prev,
        [plantId]: response.data || []
      }));
    } catch (error) {
      console.error('Error fetching shipping points:', error);
    }
  };

  // Determine plant for item following SAP priority:
  // 1. Customer Master (Ship-to Party's delivering plant)
  // 2. Material Master (plant from sales data)
  const determinePlantForItem = async (index, materialId) => {
    const shipToParty = header.ship_to_party || header.customer_id;
    if (!shipToParty || !header.sales_organization || !header.distribution_channel) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/presales/determine-plant`, {
        params: {
          material_id: materialId || undefined,
          ship_to_party: shipToParty,
          sales_org: header.sales_organization,
          dist_channel: header.distribution_channel
        }
      });
      
      if (response.data?.plant_id) {
        const newItems = [...items];
        newItems[index].plant = response.data.plant_id;
        newItems[index].plant_determined_from = response.data.source;
        newItems[index].shipping_point = ''; // Reset shipping point when plant changes
        setItems(newItems);
        
        // Fetch shipping points for this plant
        fetchShippingPointsForPlant(response.data.plant_id);
      }
    } catch (error) {
      console.error('Error determining plant:', error);
    }
  };

  const handleCustomerChange = (customerId) => {
    setSalesAreaError('');
    setCustomerSalesAreas([]);
    
    if (!customerId) {
      setHeader({
        ...header,
        customer_id: '',
        sold_to_party: '',
        ship_to_party: '',
        bill_to_party: '',
        payer: '',
        sales_organization: '',
        distribution_channel: '',
        division: '',
        incoterms: '',
        incoterms_location: '',
        payment_terms: ''
      });
      return;
    }
    
    const selectedCustomer = customers.find(c => c.customer_id === customerId);
    
    if (selectedCustomer) {
      const salesAreas = selectedCustomer.sales_area_data || [];
      
      if (salesAreas.length === 0) {
        setSalesAreaError(`Error: Customer ${customerId} is not present in any Sales Area. Please maintain Sales Area data in Customer Master.`);
        setCustomerSalesAreas([]);
      } else {
        setCustomerSalesAreas(salesAreas);
        setSalesAreaError('');
      }
    }
    
    setHeader({
      ...header,
      customer_id: customerId,
      sold_to_party: customerId,
      ship_to_party: customerId,
      sales_organization: '',
      distribution_channel: '',
      division: '',
      incoterms: '',
      incoterms_location: '',
      payment_terms: ''
    });
  };

  // Get unique sales orgs from customer's sales areas
  const getCustomerSalesOrgs = () => {
    if (customerSalesAreas.length === 0) return [];
    const uniqueOrgs = [...new Set(customerSalesAreas.map(sa => sa.sales_org_id))];
    return salesOrgs.filter(org => uniqueOrgs.includes(org.sales_org_id));
  };

  // Get distribution channels for selected sales org from customer's sales areas
  const getCustomerDistChannels = () => {
    if (customerSalesAreas.length === 0 || !header.sales_organization) return [];
    const relevantAreas = customerSalesAreas.filter(sa => sa.sales_org_id === header.sales_organization);
    const uniqueChannels = [...new Set(relevantAreas.map(sa => sa.dist_channel_id))];
    return distChannels.filter(dc => uniqueChannels.includes(dc.dist_channel_id));
  };

  // Get divisions for selected sales org and distribution channel from customer's sales areas
  const getCustomerDivisions = () => {
    if (customerSalesAreas.length === 0 || !header.sales_organization || !header.distribution_channel) return [];
    const relevantAreas = customerSalesAreas.filter(
      sa => sa.sales_org_id === header.sales_organization && sa.dist_channel_id === header.distribution_channel
    );
    const uniqueDivisions = [...new Set(relevantAreas.map(sa => sa.division_id))];
    return divisions.filter(d => uniqueDivisions.includes(d.division_id));
  };

  // Handle sales org change - reset distribution channel and division
  const handleSalesOrgChange = (salesOrgId) => {
    setHeader({
      ...header,
      sales_organization: salesOrgId,
      distribution_channel: '',
      division: '',
      incoterms: '',
      incoterms_location: '',
      payment_terms: ''
    });
  };

  // Handle distribution channel change - reset division and fetch commercial terms
  const handleDistChannelChange = (distChannelId) => {
    const newHeader = {
      ...header,
      distribution_channel: distChannelId,
      division: ''
    };
    setHeader(newHeader);
    
    // Fetch commercial terms from Ship-to Party's customer master
    const shipToParty = header.ship_to_party || header.customer_id;
    if (shipToParty && header.sales_organization && distChannelId) {
      fetchCommercialTerms(shipToParty, header.sales_organization, distChannelId);
    }
  };

  // Get ship-to party options
  const getShipToPartyOptions = () => {
    return customers;
  };

  // Handle ship-to party change - fetch commercial terms and re-determine plants
  const handleShipToPartyChange = (shipToPartyId) => {
    setHeader({
      ...header,
      ship_to_party: shipToPartyId
    });
    
    // Fetch commercial terms from Ship-to Party's customer master
    if (shipToPartyId && header.sales_organization && header.distribution_channel) {
      fetchCommercialTerms(shipToPartyId, header.sales_organization, header.distribution_channel);
    }
    
    // Re-determine plants for all items when Ship-to Party changes
    items.forEach((item, index) => {
      if (item.material_id) {
        determinePlantForItem(index, item.material_id);
      }
    });
  };

  // Get materials available for the selected Sales Org and Distribution Channel
  const getAvailableMaterials = () => {
    if (!header.sales_organization || !header.distribution_channel) {
      return [];
    }
    
    return materials.filter(m => {
      const salesData = m.sales_data || [];
      return salesData.some(
        sd => sd.sales_org_id === header.sales_organization && 
              sd.dist_channel_id === header.distribution_channel
      );
    });
  };

  // Check if a material is available in the selected sales area
  const isMaterialAvailable = (materialNumber) => {
    if (!materialNumber || !header.sales_organization || !header.distribution_channel) {
      return false;
    }
    
    const material = materials.find(m => m.material_number === materialNumber);
    if (!material) return false;
    
    const salesData = material.sales_data || [];
    return salesData.some(
      sd => sd.sales_org_id === header.sales_organization && 
            sd.dist_channel_id === header.distribution_channel
    );
  };

  // Get error message for unavailable material (SAP-style)
  const getMaterialError = (materialNumber) => {
    if (!materialNumber) return null;
    if (!header.sales_organization || !header.distribution_channel) {
      return 'Please select Sales Organization and Distribution Channel first';
    }
    
    const material = materials.find(m => m.material_number === materialNumber);
    if (!material) return `Material ${materialNumber} not found`;
    
    if (!isMaterialAvailable(materialNumber)) {
      return `Material ${materialNumber} is not defined for sales org. ${header.sales_organization}, distr.chan. ${header.distribution_channel}`;
    }
    
    return null;
  };

  const handleAddItem = () => {
    const newItemNumber = items.length > 0 ? Math.max(...items.map(i => i.item_number)) + 10 : 10;
    setItems([...items, {
      item_number: newItemNumber,
      material_id: '',
      item_description: '',
      item_category: 'TAN',
      quoted_quantity: 1,
      unit_of_measure: 'EA',
      net_price: 0,
      plant: '',
      plant_determined_from: '',
      shipping_point: '',
      pricing_info: '',
      availability_check: false,
      item_note: ''
    }]);
    setActiveItemIndex(items.length);
  };

  const handleRemoveItem = (index) => {
    if (items.length <= 1) {
      toast.error('At least one item is required');
      return;
    }
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    if (activeItemIndex >= newItems.length) {
      setActiveItemIndex(newItems.length - 1);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Auto-fill description and determine plant when material is selected
    if (field === 'material_id' && value) {
      const material = materials.find(m => m.material_number === value);
      if (material) {
        newItems[index].item_description = material.description || '';
        newItems[index].unit_of_measure = material.base_uom || 'EA';
      }
      setItems(newItems);
      // Determine plant for this item
      determinePlantForItem(index, value);
    } else if (field === 'plant' && value) {
      newItems[index].plant_determined_from = 'MANUAL';
      newItems[index].shipping_point = ''; // Reset shipping point when plant changes
      setItems(newItems);
      // Fetch shipping points for this plant
      fetchShippingPointsForPlant(value);
    } else {
      setItems(newItems);
    }
  };

  const validateForm = () => {
    if (!header.customer_id) {
      toast.error('Customer is required');
      return false;
    }
    
    if (customerSalesAreas.length === 0) {
      toast.error('Error: The Customer is not present in any Sales Area. Please maintain Sales Area data in Customer Master.');
      return false;
    }
    
    if (!header.sales_organization) {
      toast.error('Sales Organization is required');
      return false;
    }
    if (!header.distribution_channel) {
      toast.error('Distribution Channel is required');
      return false;
    }
    if (!header.division) {
      toast.error('Division is required');
      return false;
    }
    
    // Validate that selected sales area is valid for the customer
    const validSalesArea = customerSalesAreas.some(
      sa => sa.sales_org_id === header.sales_organization && 
            sa.dist_channel_id === header.distribution_channel &&
            sa.division_id === header.division
    );
    if (!validSalesArea) {
      toast.error('Error: The selected Sales Area (Sales Org + Distribution Channel + Division) is not valid for this Customer');
      return false;
    }
    
    if (!header.quotation_date) {
      toast.error('Quotation Date is required');
      return false;
    }
    if (!header.valid_from || !header.valid_to) {
      toast.error('Validity period is required');
      return false;
    }
    
    // Date validations
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Quotation date should not be in the future (can be today or past for backdating)
    if (header.quotation_date) {
      const quotationDate = new Date(header.quotation_date);
      quotationDate.setHours(0, 0, 0, 0);
      if (quotationDate > today) {
        toast.error('Quotation Date cannot be in the future');
        return false;
      }
    }
    
    // Valid From date should not be in the past
    if (header.valid_from) {
      const validFromDate = new Date(header.valid_from);
      validFromDate.setHours(0, 0, 0, 0);
      if (validFromDate < today) {
        toast.error('Valid From date cannot be in the past');
        return false;
      }
    }
    
    // Valid To date should not be in the past
    if (header.valid_to) {
      const validToDate = new Date(header.valid_to);
      validToDate.setHours(0, 0, 0, 0);
      if (validToDate < today) {
        toast.error('Valid To date cannot be in the past');
        return false;
      }
    }
    
    // Valid To must be greater than Valid From
    if (header.valid_from && header.valid_to) {
      const validFromDate = new Date(header.valid_from);
      const validToDate = new Date(header.valid_to);
      if (validToDate <= validFromDate) {
        toast.error('Valid To date must be greater than Valid From date');
        return false;
      }
    }
    
    for (const item of items) {
      if (!item.item_description) {
        toast.error(`Item ${item.item_number}: Description is required`);
        return false;
      }
      if (!item.quoted_quantity || item.quoted_quantity <= 0) {
        toast.error(`Item ${item.item_number}: Quantity must be greater than 0`);
        return false;
      }
      // Validate material is defined for the sales area
      if (item.material_id) {
        const materialError = getMaterialError(item.material_id);
        if (materialError) {
          toast.error(`Item ${item.item_number}: ${materialError}`);
          return false;
        }
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSaving(true);
    try {
      const payload = {
        ...header,
        // Convert empty strings to null for optional fields
        payment_terms: header.payment_terms || null,
        incoterms: header.incoterms || null,
        incoterms_location: header.incoterms_location || null,
        customer_reference: header.customer_reference || null,
        header_note: header.header_note || null,
        items: items.map(item => ({
          item_number: parseInt(item.item_number),
          material_id: item.material_id || null,
          item_description: item.item_description,
          item_category: item.item_category || 'TAN',
          quoted_quantity: parseInt(item.quoted_quantity) || 0,
          unit_of_measure: item.unit_of_measure || 'EA',
          net_price: parseFloat(item.net_price) || 0,
          plant: item.plant || null,
          item_note: item.item_note || null
        }))
      };
      
      console.log('Sending quotation payload:', JSON.stringify(payload, null, 2));
      
      const response = await axios.post(`${BACKEND_URL}/api/presales/quotations`, payload);
      toast.success(`Quotation '${response.data.quotation_number}' created successfully!`);
      navigate(`/presales/quotations/${response.data.quotation_number}`);
    } catch (error) {
      console.error('Error creating quotation:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map(err => 
            `${err.loc.join('.')}: ${err.msg}`
          ).join(', ');
          toast.error(`Validation error: ${errorMessages}`);
        } else {
          toast.error(error.response.data.detail);
        }
      } else {
        toast.error('Failed to create quotation. Please check all required fields.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const currentItem = items[activeItemIndex] || items[0];

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3">Loading...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="quotation-create">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/presales/quotations')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6" />
                Create Quotation
              </h1>
              <p className="text-sm text-gray-600">Create a price offer for customer with automatic pricing</p>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={isSaving} data-testid="save-quotation-btn">
            {isSaving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Create Quotation</>
            )}
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="header">Header Data</TabsTrigger>
            <TabsTrigger value="partners">Partners & Terms</TabsTrigger>
            <TabsTrigger value="items">Items ({items.length})</TabsTrigger>
          </TabsList>

          {/* Header Tab */}
          <TabsContent value="header">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Customer & Organization */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Customer & Organization</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Document Type</Label>
                    <Input value="QT - Quotation" disabled className="bg-gray-50" />
                  </div>
                  <div>
                    <Label>Customer *</Label>
                    <Select value={header.customer_id || '__SELECT__'} onValueChange={(v) => handleCustomerChange(v === '__SELECT__' ? '' : v)}>
                      <SelectTrigger data-testid="customer-select"><SelectValue placeholder="Select Customer" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__SELECT__">Select Customer</SelectItem>
                        {customers.map(c => (
                          <SelectItem key={c.customer_id} value={c.customer_id}>
                            {c.customer_id} - {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {salesAreaError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {salesAreaError}
                    </div>
                  )}
                  
                  {header.customer_id && customerSalesAreas.length > 0 && (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 text-sm">
                      Customer has {customerSalesAreas.length} Sales Area(s) configured
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Sales Organization *</Label>
                      <Select 
                        value={header.sales_organization || '__SELECT__'} 
                        onValueChange={(v) => handleSalesOrgChange(v === '__SELECT__' ? '' : v)}
                        disabled={!header.customer_id || customerSalesAreas.length === 0}
                      >
                        <SelectTrigger data-testid="sales-org-select">
                          <SelectValue placeholder={!header.customer_id ? "Select Customer first" : "Select Sales Org"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__SELECT__">Select Sales Org</SelectItem>
                          {getCustomerSalesOrgs().map(org => (
                            <SelectItem key={org.sales_org_id} value={org.sales_org_id}>
                              {org.sales_org_id} - {org.sales_org_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Distribution Channel *</Label>
                      <Select 
                        value={header.distribution_channel || '__SELECT__'} 
                        onValueChange={(v) => handleDistChannelChange(v === '__SELECT__' ? '' : v)}
                        disabled={!header.sales_organization}
                      >
                        <SelectTrigger data-testid="dist-channel-select">
                          <SelectValue placeholder={!header.sales_organization ? "Select Sales Org first" : "Select Channel"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__SELECT__">Select Channel</SelectItem>
                          {getCustomerDistChannels().map(dc => (
                            <SelectItem key={dc.dist_channel_id} value={dc.dist_channel_id}>
                              {dc.dist_channel_id} - {dc.dist_channel_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Division *</Label>
                    <Select 
                      value={header.division || '__SELECT__'} 
                      onValueChange={(v) => setHeader({...header, division: v === '__SELECT__' ? '' : v})}
                      disabled={!header.distribution_channel}
                    >
                      <SelectTrigger data-testid="division-select">
                        <SelectValue placeholder={!header.distribution_channel ? "Select Distribution Channel first" : "Select Division"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__SELECT__">Select Division</SelectItem>
                        {getCustomerDivisions().map(d => (
                          <SelectItem key={d.division_id} value={d.division_id}>
                            {d.division_id} - {d.division_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {header.division && (
                      <p className="text-xs text-green-600 mt-1">
                        Sales Area: {header.sales_organization} / {header.distribution_channel} / {header.division}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <Label>Pricing Procedure</Label>
                    <Select value={header.pricing_procedure} onValueChange={(v) => setHeader({...header, pricing_procedure: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {pricingProcedures.map(proc => (
                          <SelectItem key={proc.pricing_procedure_id} value={proc.pricing_procedure_id}>
                            {proc.pricing_procedure_id} - {proc.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>

              {/* Dates & Validity */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Dates & Validity</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Quotation Date *</Label>
                      <Input
                        type="date"
                        value={header.quotation_date}
                        onChange={(e) => setHeader({...header, quotation_date: e.target.value})}
                        max={new Date().toISOString().split('T')[0]}
                        data-testid="quotation-date"
                      />
                      <p className="text-xs text-gray-500 mt-1">Document date (cannot be future)</p>
                    </div>
                    <div>
                      <Label>Pricing Date</Label>
                      <Input
                        type="date"
                        value={header.pricing_date}
                        onChange={(e) => setHeader({...header, pricing_date: e.target.value})}
                        data-testid="pricing-date"
                      />
                      <p className="text-xs text-gray-500 mt-1">Defaults to today</p>
                    </div>
                  </div>
                  <div>
                    <Label>Requested Delivery Date</Label>
                    <Input
                      type="date"
                      value={header.requested_delivery_date}
                      onChange={(e) => setHeader({...header, requested_delivery_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="requested-delivery-date"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Valid From *</Label>
                      <Input
                        type="date"
                        value={header.valid_from}
                        onChange={(e) => setHeader({...header, valid_from: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                      />
                      <p className="text-xs text-gray-500 mt-1">Cannot be in the past</p>
                      {header.valid_from && new Date(header.valid_from) < new Date(new Date().toISOString().split('T')[0]) && (
                        <p className="text-xs text-red-600 mt-1">Date is in the past</p>
                      )}
                    </div>
                    <div>
                      <Label>Valid To *</Label>
                      <Input
                        type="date"
                        value={header.valid_to}
                        onChange={(e) => setHeader({...header, valid_to: e.target.value})}
                        min={header.valid_from || new Date().toISOString().split('T')[0]}
                      />
                      <p className="text-xs text-gray-500 mt-1">Must be greater than Valid From</p>
                      {header.valid_from && header.valid_to && new Date(header.valid_to) <= new Date(header.valid_from) && (
                        <p className="text-xs text-red-600 mt-1">Must be after Valid From date</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>Customer Reference</Label>
                    <Input
                      value={header.customer_reference}
                      onChange={(e) => setHeader({...header, customer_reference: e.target.value})}
                      placeholder="Customer's reference number"
                      maxLength={35}
                    />
                  </div>
                  <div>
                    <Label>Header Note</Label>
                    <textarea
                      value={header.header_note}
                      onChange={(e) => setHeader({...header, header_note: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                      rows={3}
                      placeholder="Additional notes..."
                      maxLength={1000}
                    />
                  </div>
                </div>
              </Card>
            </div>
            
            {/* Pricing Note */}
            <Card className="mt-6 p-4 bg-orange-50 border-orange-200">
              <div className="flex items-center gap-2 text-orange-800">
                <Calculator className="w-5 h-5" />
                <span className="font-medium">Automatic Pricing</span>
              </div>
              <p className="text-sm text-orange-700 mt-1">
                Pricing will be calculated automatically when the quotation is saved, using the selected Pricing Procedure ({header.pricing_procedure}).
              </p>
            </Card>
          </TabsContent>

          {/* Partners & Terms Tab */}
          <TabsContent value="partners">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Partner Functions */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Partner Functions
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label>Sold-to Party</Label>
                    <Input 
                      value={header.sold_to_party ? `${header.sold_to_party} - ${customers.find(c => c.customer_id === header.sold_to_party)?.name || ''}` : ''} 
                      disabled 
                      className="bg-gray-50" 
                    />
                    <p className="text-xs text-gray-500 mt-1">Same as Customer</p>
                  </div>
                  <div>
                    <Label>Ship-to Party</Label>
                    <Select 
                      value={header.ship_to_party || '__DEFAULT__'} 
                      onValueChange={(v) => handleShipToPartyChange(v === '__DEFAULT__' ? header.customer_id : v)}
                      disabled={!header.customer_id}
                    >
                      <SelectTrigger data-testid="ship-to-party-select">
                        <SelectValue placeholder="Select Ship-to Party" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__DEFAULT__">Same as Sold-to Party</SelectItem>
                        {getShipToPartyOptions().map(c => (
                          <SelectItem key={c.customer_id} value={c.customer_id}>
                            {c.customer_id} - {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Commercial terms will be fetched from Ship-to Party's Customer Master
                    </p>
                  </div>
                </div>
              </Card>

              {/* Commercial Terms */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Commercial Terms</h3>
                <div className="space-y-4">
                  {header.sales_organization && header.distribution_channel && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm flex items-start gap-2">
                      <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>These terms are automatically fetched from <strong>Ship-to Party's</strong> Customer Master for the selected Sales Area. You can override them if needed.</span>
                    </div>
                  )}
                  <div>
                    <Label>Incoterms</Label>
                    <Select 
                      value={header.incoterms || '__NONE__'} 
                      onValueChange={(v) => setHeader({...header, incoterms: v === '__NONE__' ? '' : v})}
                    >
                      <SelectTrigger data-testid="incoterms-select">
                        <SelectValue placeholder="Select Incoterms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- None --</SelectItem>
                        {incoterms.map(i => (
                          <SelectItem key={i.incoterms_id} value={i.incoterms_id}>
                            {i.incoterms_id} - {i.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Incoterms Location</Label>
                    <Input
                      value={header.incoterms_location}
                      onChange={(e) => setHeader({...header, incoterms_location: e.target.value})}
                      placeholder="e.g., Port of destination"
                      maxLength={70}
                    />
                  </div>
                  <div>
                    <Label>Payment Terms</Label>
                    <Select 
                      value={header.payment_terms || '__NONE__'} 
                      onValueChange={(v) => setHeader({...header, payment_terms: v === '__NONE__' ? '' : v})}
                    >
                      <SelectTrigger data-testid="payment-terms-select">
                        <SelectValue placeholder="Select Payment Terms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- None --</SelectItem>
                        {paymentTerms.map(p => (
                          <SelectItem key={p.payment_key} value={p.payment_key}>
                            {p.payment_key} - {p.sales_text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Items Tab */}
          <TabsContent value="items">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Item List */}
              <Card className="p-4 lg:col-span-1">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Items</h3>
                  <Button size="sm" onClick={handleAddItem}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {items.map((item, index) => (
                    <div
                      key={`item-${item.item_number || index}`}
                      className={`p-3 rounded-lg cursor-pointer flex justify-between items-center ${
                        activeItemIndex === index ? 'bg-orange-100 border-2 border-orange-500' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      onClick={() => setActiveItemIndex(index)}
                    >
                      <div>
                        <p className="font-mono font-bold text-sm">{item.item_number.toString().padStart(4, '0')}</p>
                        <p className="text-xs text-gray-600 truncate max-w-[120px]">
                          {item.item_description || item.material_id || 'New Item'}
                        </p>
                      </div>
                      {items.length > 1 && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleRemoveItem(index); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              {/* Item Details */}
              <Card className="p-6 lg:col-span-3">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Item {currentItem.item_number.toString().padStart(4, '0')} Details
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Material</Label>
                    <Select 
                      value={currentItem.material_id || '__NONE__'} 
                      onValueChange={(v) => handleItemChange(activeItemIndex, 'material_id', v === '__NONE__' ? '' : v)}
                      disabled={!header.sales_organization || !header.distribution_channel}
                    >
                      <SelectTrigger data-testid="item-material-select">
                        <SelectValue placeholder={
                          !header.sales_organization || !header.distribution_channel 
                            ? "Select Sales Area first" 
                            : "Select Material"
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- Manual Entry --</SelectItem>
                        {getAvailableMaterials().length > 0 ? (
                          getAvailableMaterials().map(m => (
                            <SelectItem key={m.material_number} value={m.material_number}>
                              {m.material_number} - {m.description}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="__NO_MATERIALS__" disabled>
                            No materials for this Sales Area
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {header.sales_organization && header.distribution_channel && (
                      <p className="text-xs text-gray-500 mt-1">
                        {getAvailableMaterials().length} material(s) available for {header.sales_organization}/{header.distribution_channel}
                      </p>
                    )}
                    {currentItem.material_id && getMaterialError(currentItem.material_id) && (
                      <p className="text-xs text-red-600 mt-1 p-2 bg-red-50 rounded">
                        {getMaterialError(currentItem.material_id)}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Item Category</Label>
                    <Select value={currentItem.item_category} onValueChange={(v) => handleItemChange(activeItemIndex, 'item_category', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TAN">TAN - Standard Item</SelectItem>
                        <SelectItem value="TANN">TANN - Free Item</SelectItem>
                        <SelectItem value="TAD">TAD - Service Item</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Description *</Label>
                    <Input
                      value={currentItem.item_description}
                      onChange={(e) => handleItemChange(activeItemIndex, 'item_description', e.target.value)}
                      placeholder="Item description"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <Label>Quoted Quantity *</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={currentItem.quoted_quantity}
                      onChange={(e) => {
                        const val = e.target.value;
                        // Only allow whole numbers
                        if (val === '' || /^\d+$/.test(val)) {
                          handleItemChange(activeItemIndex, 'quoted_quantity', val);
                        }
                      }}
                      onKeyDown={(e) => {
                        // Prevent decimal point and 'e' for scientific notation
                        if (e.key === '.' || e.key === 'e' || e.key === 'E' || e.key === '-') {
                          e.preventDefault();
                        }
                      }}
                      placeholder="Enter quantity"
                    />
                    <p className="text-xs text-gray-500 mt-1">Whole numbers only</p>
                  </div>
                  <div>
                    <Label>Unit of Measure</Label>
                    <Input
                      value={currentItem.unit_of_measure || 'EA'}
                      disabled
                      className="bg-gray-50 text-gray-700"
                    />
                    <p className="text-xs text-gray-500 mt-1">Derived from material master</p>
                  </div>
                  
                  {/* Plant & Shipping Point Section */}
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      Logistics
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Plant</Label>
                        <Select 
                          value={currentItem.plant || '__NONE__'} 
                          onValueChange={(v) => handleItemChange(activeItemIndex, 'plant', v === '__NONE__' ? '' : v)}
                        >
                          <SelectTrigger data-testid="item-plant-select">
                            <SelectValue placeholder="Select Plant" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__NONE__">-- None --</SelectItem>
                            {plants.map(p => (
                              <SelectItem key={p.plant_id} value={p.plant_id}>
                                {p.plant_id} - {p.plant_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {currentItem.plant_determined_from && (
                          <p className="text-xs text-green-600 mt-1">
                            Determined from: {currentItem.plant_determined_from === 'SHIP_TO_PARTY' ? 'Ship-to Party' : 
                              currentItem.plant_determined_from === 'MATERIAL_MASTER' ? 'Material Master' : 'Manual'}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label>Shipping Point</Label>
                        <Select 
                          value={currentItem.shipping_point || '__NONE__'} 
                          onValueChange={(v) => handleItemChange(activeItemIndex, 'shipping_point', v === '__NONE__' ? '' : v)}
                          disabled={!currentItem.plant}
                        >
                          <SelectTrigger data-testid="item-shipping-point-select">
                            <SelectValue placeholder={!currentItem.plant ? "Select Plant first" : "Select Shipping Point"} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__NONE__">-- None --</SelectItem>
                            {(shippingPointsForPlant[currentItem.plant] || []).length > 0 ? (
                              (shippingPointsForPlant[currentItem.plant] || []).map(sp => (
                                <SelectItem key={sp.shipping_point_id} value={sp.shipping_point_id}>
                                  {sp.shipping_point_id} - {sp.name}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="__NO_SP__" disabled>
                                No shipping points for this plant
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 mt-1">Must be assigned to Plant</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Material Pricing Info - Shows after material selection */}
                  {currentItem.material_id && (
                    <div className="col-span-2 border-t pt-4 mt-2">
                      <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <Calculator className="w-4 h-4" />
                        Material Pricing Information
                      </h4>
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        {(() => {
                          const material = materials.find(m => m.material_number === currentItem.material_id);
                          if (!material) return <p className="text-sm text-gray-600">Material not found</p>;
                          
                          const salesData = material.sales_data?.find(
                            sd => sd.sales_org_id === header.sales_organization && 
                                  sd.dist_channel_id === header.distribution_channel
                          );
                          
                          return (
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-gray-600">Material:</span>
                                <p className="font-medium">{material.material_number}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">Base UoM:</span>
                                <p className="font-medium">{material.base_uom || 'EA'}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">Sales Price:</span>
                                <p className="font-medium text-green-600">
                                  {salesData?.sales_price ? `$${salesData.sales_price}` : 'From Condition Records'}
                                </p>
                              </div>
                            </div>
                          );
                        })()}
                        <p className="text-xs text-orange-700 mt-2">
                          Final pricing will be calculated using Pricing Procedure: <strong>{header.pricing_procedure}</strong>
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Pricing Section */}
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <h4 className="font-medium text-gray-700 mb-3">Pricing Override</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Manual Net Price (Override)</Label>
                        <Input
                          type="text"
                          value={currentItem.net_price}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Allow empty, numbers, and decimals
                            if (val === '' || /^\d*\.?\d*$/.test(val)) {
                              handleItemChange(activeItemIndex, 'net_price', val);
                            }
                          }}
                          placeholder="Leave 0 for auto-pricing"
                        />
                        <p className="text-xs text-gray-500 mt-1">Enter 0 to use automatic pricing from condition records</p>
                      </div>
                      <div>
                        <Label>Pricing Information</Label>
                        <Input
                          value={currentItem.pricing_info}
                          onChange={(e) => handleItemChange(activeItemIndex, 'pricing_info', e.target.value)}
                          placeholder="Optional pricing notes"
                          maxLength={500}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Availability Check */}
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="availability-check"
                        checked={currentItem.availability_check}
                        onCheckedChange={(checked) => handleItemChange(activeItemIndex, 'availability_check', checked)}
                      />
                      <Label htmlFor="availability-check" className="cursor-pointer">
                        Perform Availability Check (Optional)
                      </Label>
                    </div>
                  </div>
                  
                  <div className="col-span-2">
                    <Label>Item Note</Label>
                    <textarea
                      value={currentItem.item_note}
                      onChange={(e) => handleItemChange(activeItemIndex, 'item_note', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                      rows={2}
                      placeholder="Additional notes..."
                      maxLength={500}
                    />
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default QuotationCreate;
